const QUARTER_REGEXES = [
    /(\d{4})\s*q([1-4])/i,
    /(\d{4})\s*-\s*q([1-4])/i,
    /t([1-4])\s*(\d{4})/i,
];

const toNumber = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'string' && value.trim() === '') {
        return null;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const parseQuarter = (value) => {
    const text = String(value || '').trim();
    for (let i = 0; i < QUARTER_REGEXES.length; i += 1) {
        const match = QUARTER_REGEXES[i].exec(text);
        if (!match) {
            continue;
        }

        if (i === 2) {
            return {
                year: Number(match[2]),
                quarter: Number(match[1]),
            };
        }

        return {
            year: Number(match[1]),
            quarter: Number(match[2]),
        };
    }

    return { year: 0, quarter: 0 };
};

const sortQuarterAsc = (points = []) => {
    const source = Array.isArray(points) ? points : [];
    return source.slice().sort((left, right) => {
        const l = parseQuarter(left?.quarter);
        const r = parseQuarter(right?.quarter);
        if (l.year !== r.year) {
            return l.year - r.year;
        }

        return l.quarter - r.quarter;
    });
};

const countFiniteValues = (values = []) => {
    return values.reduce((count, value) => {
        return toNumber(value) === null ? count : count + 1;
    }, 0);
};

const isSeriesEligible = (series, minimumPoints = 2) => {
    if (!series || !Array.isArray(series.labels) || !Array.isArray(series.values)) {
        return false;
    }

    if (series.labels.length < minimumPoints || series.values.length < minimumPoints) {
        return false;
    }

    return countFiniteValues(series.values) >= minimumPoints;
};

const makeLineSeries = (points = [], valueKey = 'yoyChangePp') => {
    const sorted = sortQuarterAsc(points);

    const valueKeys = Array.isArray(valueKey) ? valueKey : [valueKey];

    return {
        labels: sorted.map((point) => point?.quarter || ''),
        values: sorted.map((point) => {
            for (let index = 0; index < valueKeys.length; index += 1) {
                const key = valueKeys[index];
                const number = toNumber(point?.[key]);
                if (number !== null) {
                    return number;
                }
            }
            return null;
        }),
    };
};

const makeBarSeries = (rows = []) => {
    const clean = rows
        .map((row) => ({
            label: String(row?.label || row?.name || '').trim(),
            value: toNumber(row?.value ?? row?.indexValue ?? row?.yoyChangePp ?? row?.qoqChangePp),
        }))
        .filter((row) => row.label && row.value !== null)
        .slice(0, 8);

    return {
        labels: clean.map((row) => row.label),
        values: clean.map((row) => row.value),
    };
};

const canUseChart = () => typeof window !== 'undefined' && typeof window.Chart === 'function';

const formatValue = (value, mode, suffix = '') => {
    const number = toNumber(value);
    if (number === null) {
        return '';
    }

    if (mode === 'percent') {
        const sign = number > 0 ? '+' : '';
        const formatted = new Intl.NumberFormat('sr-RS', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(number);
        return `${sign}${formatted}${suffix || '%'}`;
    }

    const formatted = new Intl.NumberFormat('sr-RS', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(number);

    return `${formatted}${suffix}`;
};

const axisFormatConfig = (chartModel, axisId = 'y') => {
    const axisMeta = chartModel?.axes?.[axisId] || null;
    const fallbackMode = chartModel?.yValueMode || 'number';
    const fallbackSuffix = chartModel?.ySuffix || '';

    return {
        mode: axisMeta?.valueMode || fallbackMode,
        suffix: axisMeta?.suffix || fallbackSuffix,
    };
};

const createOfficialChart = (canvas, chartModel) => {
    if (!canvas || !canUseChart() || !chartModel) {
        return null;
    }

    const context = canvas.getContext('2d');
    if (!context) {
        return null;
    }

    return new window.Chart(context, {
        type: chartModel.type || 'line',
        data: {
            labels: chartModel.labels || [],
            datasets: chartModel.datasets || [],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: {
                    display: Array.isArray(chartModel.datasets) && chartModel.datasets.length > 1,
                    labels: {
                        usePointStyle: true,
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (contextItem) => {
                            const value = toNumber(contextItem?.parsed?.y);
                            if (value === null) {
                                return '';
                            }
                            const datasetAxisId = contextItem?.dataset?.yAxisID || 'y';
                            const formatConfig = axisFormatConfig(chartModel, datasetAxisId);
                            return `${contextItem.dataset.label || ''}: ${formatValue(value, formatConfig.mode, formatConfig.suffix)}`.trim();
                        },
                    },
                },
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: true,
                        maxRotation: 0,
                    },
                    grid: {
                        color: 'rgba(17, 31, 42, 0.07)',
                    },
                },
                y: {
                    beginAtZero: false,
                    ticks: {
                        callback: (value) => {
                            const formatConfig = axisFormatConfig(chartModel, 'y');
                            return formatValue(value, formatConfig.mode, formatConfig.suffix);
                        },
                    },
                    grid: {
                        color: 'rgba(17, 31, 42, 0.07)',
                    },
                },
                ...(chartModel?.axes?.y1?.enabled
                    ? {
                        y1: {
                            beginAtZero: false,
                            position: 'right',
                            ticks: {
                                callback: (value) => {
                                    const formatConfig = axisFormatConfig(chartModel, 'y1');
                                    return formatValue(value, formatConfig.mode, formatConfig.suffix);
                                },
                            },
                            grid: {
                                drawOnChartArea: false,
                            },
                        },
                    }
                    : {}),
            },
        },
    });
};

const deferInit = (element, callback) => {
    if (!element || typeof callback !== 'function') {
        return;
    }

    if (!('IntersectionObserver' in window)) {
        callback();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                observer.disconnect();
                callback();
            }
        });
    }, { rootMargin: '160px' });

    observer.observe(element);
};

const exported = {
    sortQuarterAsc,
    makeLineSeries,
    makeBarSeries,
    isSeriesEligible,
    createOfficialChart,
    deferInit,
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = exported;
}

export {
    sortQuarterAsc,
    makeLineSeries,
    makeBarSeries,
    isSeriesEligible,
    createOfficialChart,
    deferInit,
};
